import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ChipTab,
  Divider,
  EmptyState,
  IconButton,
  List,
  ListItem,
  ListItemText,
  RefreshIcon,
  SearchField,
  Skeleton,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import DocumentsPagination from '../DocumentsPagination/DocumentsPagination';
import DocumentIndexingItem from '../DocumentIndexingItem/DocumentIndexingItem';
import DocumentContentModal from '../DocumentContentModal/DocumentContentModal';
import DocumentIndexingErrorModal from '../DocumentIndexingErrorModal/DocumentIndexingErrorModal';
import { type Props, useDocumentIndexingSettingsCard } from './useDocumentIndexingSettingsCard';

const SKELETON_ROWS = [0, 1, 2, 3, 4];


export default function DocumentIndexingSettingsCard(props: Props) {
  const hook = useDocumentIndexingSettingsCard(props);

  return (
    <>
      <Card>
        <CardHeader
          title="Indexation des documents"
          subheader="Suivi de l'extraction de texte et de l'indexation pour l'assistant et la recherche intelligente."
          action={(
            <IconButton
              size="small"
              tooltip="Actualiser la liste"
              onClick={hook.handleRefresh}
              disabled={hook.isRefreshing}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
        />
        <Divider />

        <CardContent>
          <Stack spacing={2}>
            {/* Last Indexation & Summary Row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 16,
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 16,
                borderRadius: 8,
                border: '1px solid #e0e0e0',
                backgroundColor: '#fff',
              }}
            >
              <Stack spacing={0.5}>
                <Typography variant="body2Emphasis">Dernière indexation</Typography>
                <Typography variant="body2">
                  {hook.formattedLastIndexation ? (
                    hook.formattedLastIndexation
                  ) : hook.isInitialLoading ? (
                    <Skeleton width={180} height={20} />
                  ) : (
                    'Aucune indexation effectuée'
                  )}
                </Typography>
              </Stack>


              <Stack direction="row" spacing={1} wrap="wrap">
                <Tag
                  color="default"
                  label={`${hook.stats?.total ?? 0} au total`}
                />
                <Tag
                  color="done"
                  variantIcon="state"
                  label={`${hook.stats?.done ?? 0} indexé${(hook.stats?.done ?? 0) > 1 ? 's' : ''}`}
                />
                <Tag
                  color="pending"
                  variantIcon="state"
                  label={`${hook.stats?.pending ?? 0} en attente`}
                />
                {(hook.stats?.error ?? 0) > 0 ? (
                  <Tag
                    color="cancelled"
                    variantIcon="state"
                    label={`${hook.stats?.error ?? 0} en erreur`}
                  />
                ) : null}
                {(hook.stats?.unsupported ?? 0) > 0 ? (
                  <Tag
                    color="default"
                    variantIcon="state"
                    label={`${hook.stats?.unsupported ?? 0} non pris en charge`}
                  />
                ) : null}
              </Stack>
            </div>

            {/* Filter controls */}
            <Stack spacing={2}>
              <SearchField
                value={hook.search}
                onChange={hook.handleSearchChange}
                label="Rechercher un document"
                placeholder="Rechercher par nom de fichier ou document..."
              />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 8,
                  alignItems: 'center',
                  overflowX: 'auto',
                  paddingBottom: 4,
                  maxWidth: '100%',
                }}
              >
                <ChipTab
                  label="Tous"
                  count={hook.stats?.total}
                  selected={hook.statusFilter === 'all'}
                  onClick={() => hook.handleStatusFilterChange('all')}
                />
                <ChipTab
                  label="Indexés"
                  count={hook.stats?.done}
                  selected={hook.statusFilter === 'done'}
                  onClick={() => hook.handleStatusFilterChange('done')}
                />
                <ChipTab
                  label="En attente"
                  count={hook.stats?.pending}
                  selected={hook.statusFilter === 'pending'}
                  onClick={() => hook.handleStatusFilterChange('pending')}
                />
                <ChipTab
                  label="En erreur"
                  count={hook.stats?.error}
                  selected={hook.statusFilter === 'error'}
                  onClick={() => hook.handleStatusFilterChange('error')}
                />
                <ChipTab
                  label="Non pris en charge"
                  count={hook.stats?.unsupported}
                  selected={hook.statusFilter === 'unsupported'}
                  onClick={() => hook.handleStatusFilterChange('unsupported')}
                />
              </div>
            </Stack>

          </Stack>
        </CardContent>

        <Divider />

        {/* Content Section */}
        {hook.isError ? (
          <EmptyState
            title="Indexations indisponibles"
            subtitle="Le chargement des informations d'indexation a échoué. Rechargez la page ou réessayez ultérieurement."
            action={(
              <Button variant="contained" size="small" onClick={hook.handleRefresh}>
                Réessayer
              </Button>
            )}
          />
        ) : hook.isInitialLoading ? (
          <List aria-label="Indexation des documents" aria-busy disablePadding>
            {SKELETON_ROWS.map((row) => (
              <ListItem key={row} divider aria-hidden="true">
                <ListItemText
                  disableTypography
                  primary={<Skeleton variant="text" width="40%" height={24} />}
                  secondary={<Skeleton variant="text" width="25%" height={18} />}
                />
              </ListItem>
            ))}
          </List>
        ) : hook.isOverallEmpty ? (
          <EmptyState
            title="Aucun document"
            subtitle="Les documents ajoutés à votre copropriété apparaîtront ici et seront indexés automatiquement."
          />
        ) : hook.hasNoResult ? (
          <EmptyState
            title="Aucun document trouvé"
            subtitle="Aucun document ne correspond à vos filtres actuels."
            action={(
              <Button variant="text" size="small" color="default" onClick={hook.handleResetFilters}>
                Effacer les filtres
              </Button>
            )}
          />
        ) : (
          <>
            <List aria-label="Liste des indexations de documents" disablePadding>
              {hook.items.map((item, index) => (
                <DocumentIndexingItem
                  key={item.documentId}
                  item={item}
                  divider={index < hook.items.length - 1}
                  onViewContent={hook.handleOpenContentModal}
                  onViewError={hook.handleOpenErrorModal}
                />
              ))}
            </List>

            <Divider />

            <DocumentsPagination
              count={hook.totalCount}
              page={hook.page}
              rowsPerPage={hook.pageSize}
              onPageChange={hook.handlePageChange}
              onRowsPerPageChange={hook.handleRowsPerPageChange}
            />
          </>
        )}
      </Card>

      {/* Modals */}
      <DocumentContentModal
        open={Boolean(hook.selectedContentItem)}
        documentId={hook.selectedContentItem?.documentId ?? null}
        documentName={hook.selectedContentItem?.name ?? ''}
        fileUrl={hook.selectedContentItem?.fileUrl}
        onClose={hook.handleCloseContentModal}
      />

      <DocumentIndexingErrorModal
        open={Boolean(hook.selectedErrorItem)}
        item={hook.selectedErrorItem}
        onClose={hook.handleCloseErrorModal}
      />
    </>
  );
}
